import useSWRMutation from 'swr/mutation';
import useSWR, { SWRConfiguration } from 'swr';

import { useApi } from '../use-api';
import { Assessment } from '../../types/assessment';
import {
  EventReportResponse,
  EventReportActionPlan,
  EventReportMeasurables,
  EventReportTaskResponse,
  EventReportHeaderResponse,
  EventReportFeedbackResponse,
  EventReportRatingCompetencies,
  EventReportGameDetailResponse,
  EventReportLeaderboardResponse,
  EventReportFeedbackDetailResponse,
  EventReportParticipantCompetencies,
  EventReportAssessmentDetailResponse
} from '../../types/responses/event-report-response';
import { GetAllAsync, GetAsync } from '../../services/api';
import { AssessmentTeam } from '../../types/assessment-team';
import { KendoGridResult } from '../../types/KendoGridResult';
import { AssessmentState } from '../../types/assessment-state';

const ReportsEndpoint = `events/reports`;

export const useGetEventReports = (
  params?: string,
  options?: SWRConfiguration
) => {
  const { data, error, isLoading, mutate } = useSWR<
    KendoGridResult<EventReportResponse>
  >(`${ReportsEndpoint}?${params}`, GetAllAsync, options);

  if (error) console.log(error);

  return {
    data,
    isLoading,
    isError: error,
    mutate
  };
};

export const useGetEventReportHeader = (
  eventId?: string,
  options?: SWRConfiguration
) => {
  const { data, error, isLoading, mutate } = useSWR<EventReportHeaderResponse>(
    eventId ? `${ReportsEndpoint}/${eventId}/headers` : null,
    GetAsync,
    options
  );

  if (error) console.log(error);

  return {
    data,
    isLoading,
    isError: error,
    mutate
  };
};

type EventReportDetailType<
  T extends 'activities' | 'assessments' | 'feedbacks'
> = T extends 'activities'
  ? EventReportGameDetailResponse
  : T extends 'assessments'
    ? EventReportAssessmentDetailResponse
    : EventReportFeedbackDetailResponse;

export const useGetEventReportDetail = <
  T extends 'activities' | 'assessments' | 'feedbacks'
>({
  eventId,
  type,
  playlistId,
  options
}: {
  eventId?: string;
  playlistId?: string;
  type: T;
  options?: SWRConfiguration;
}) => {
  const { data, error, isLoading, mutate } = useSWR<EventReportDetailType<T>>(
    eventId && playlistId
      ? `${ReportsEndpoint}/${eventId}/${type}/${playlistId}/details`
      : null,
    GetAsync,
    options
  );

  if (error) console.log(error);

  return {
    data,
    isLoading,
    isError: error,
    mutate
  };
};

export const useGetEventReportGameTab = ({
  tab,
  eventId,
  options,
  playlistId,
  params
}: {
  tab: 'tasks' | 'players' | 'teams' | 'groups' | 'resources';
  params?: string;
  eventId?: string;
  playlistId?: string;
  options?: SWRConfiguration;
}) => {
  const { data, error, isLoading, mutate } = useSWR<
    KendoGridResult<EventReportTaskResponse>
  >(
    eventId && playlistId
      ? `${ReportsEndpoint}/${eventId}/activities/${playlistId}/${tab}?${params}`
      : null,
    GetAllAsync,
    options
  );

  if (error) console.log(error);

  return {
    data,
    isLoading,
    isError: error,
    mutate
  };
};

type EventReportCompetenciesType<T extends 'participants' | 'ratings-summary'> =
  T extends 'participants'
    ? EventReportParticipantCompetencies
    : EventReportRatingCompetencies;

export const useGetEventReportCompetencies = <
  T extends 'participants' | 'ratings-summary'
>({
  type,
  params,
  eventId,
  options,
  playlistId
}: {
  params?: string;
  eventId?: string;
  playlistId?: string;
  options?: SWRConfiguration;
  type: T;
}) => {
  const { data, error, isLoading, mutate } = useSWR<
    KendoGridResult<EventReportCompetenciesType<T>>
  >(
    eventId && playlistId
      ? `${ReportsEndpoint}/${eventId}/assessments/${playlistId}/competencies/${type}?${params}`
      : null,
    GetAllAsync,
    options
  );

  if (error) console.log(error);

  return {
    data,
    isLoading,
    isError: error,
    mutate
  };
};

type EventReportActionMeasurableType<T extends 'actions' | 'measurables'> =
  T extends 'actions' ? EventReportActionPlan : EventReportMeasurables;

export const useGetEventReportactions = <T extends 'actions' | 'measurables'>({
  type,
  params,
  eventId,
  options,
  playlistId
}: {
  type: T;
  params?: string;
  eventId?: string;
  playlistId?: string;
  options?: SWRConfiguration;
}) => {
  const { data, error, isLoading, mutate } = useSWR<
    KendoGridResult<EventReportActionMeasurableType<T>>
  >(
    eventId && playlistId
      ? `${ReportsEndpoint}/${eventId}/assessments/${playlistId}/${type}?${params}`
      : null,
    GetAllAsync,
    options
  );

  if (error) console.log(error);

  return {
    data,
    isLoading,
    isError: error,
    mutate
  };
};

export const useGetEventReportResponseFeedback = ({
  params,
  eventId,
  options,
  eventFeedbackId
}: {
  params?: string;
  eventId?: string;
  eventFeedbackId?: string;
  options?: SWRConfiguration;
}) => {
  const { data, error, isLoading, mutate } = useSWR<
    KendoGridResult<EventReportFeedbackResponse>
  >(
    eventId && eventFeedbackId
      ? `${ReportsEndpoint}/${eventId}/feedbacks/${eventFeedbackId}/responses?${params}`
      : null,
    GetAllAsync,
    options
  );

  if (error) console.log(error);

  return {
    data,
    isLoading,
    isError: error,
    mutate
  };
};

export const useGetAssessmentDocumentAndStateByReportId = (
  eventId: string,
  reportId: string,
  shouldFetch: boolean,
  options?: SWRConfiguration
) => {
  const api = useApi();

  const key = (id: string) =>
    shouldFetch && eventId && reportId
      ? `events/${eventId}/reports/assessment/${reportId}/${id}`
      : null;

  const {
    data: document,
    error: documentError,
    isLoading: documentLoading
  } = useSWR<Assessment>(key('document'), api.fetchers.apiWithAuth, options);

  const {
    data: state,
    error: stateError,
    isLoading: stateLoading
  } = useSWR<AssessmentState>(key('state'), api.fetchers.apiWithAuth, options);

  const {
    data: teams,
    error: teamsError,
    isLoading: teamsLoading
  } = useSWR<AssessmentTeam[]>(key('teams'), api.fetchers.apiWithAuth, options);

  if (documentError) console.error(documentError);
  if (stateError) console.error(stateError);
  if (teamsError) console.error(teamsError);

  return {
    document,
    state,
    teams,
    isLoading: documentLoading || stateLoading || teamsLoading,
    isError: documentError || stateError || teamsError
  };
};

export const useGetLeaderboardData = () => {
  const { data, error, isMutating, trigger } = useSWRMutation<
    EventReportLeaderboardResponse,
    Error,
    string,
    { eventId: string; playlistId: string }
  >(
    'leaderboard-report',
    (_, { arg }) =>
      GetAsync(
        `${ReportsEndpoint}/${arg.eventId}/activities/${arg.playlistId}/leaderboard`
      ),
    {
      revalidate: false
    }
  );

  return { data, error, isMutating, trigger };
};
